import api from '@crm-src/lib/api'
import { saveBlob } from '../../lib/download'

export interface SystemActivity {
  id: string
  activity_type: string
  title: string
  description?: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show'
  activity_date?: string
  due_date?: string
  created_at: string
  updated_at: string
  
  // User who performed the activity
  user: {
    id: number
    full_name: string
    email: string
  }
  
  // Related entities
  customer?: {
    id: number
    full_name: string
    company_name: string
    customer_id: string
  }
  
  customer_deal?: {
    id: number
    title: string
    amount?: number
  }
  
  invoice?: {
    id: number
    invoice_number: string
    amount?: number
  }
  
  // Provenance. Every row ActivityLogger writes carries source: 'system';
  // an activity a person entered by hand has neither. See xop-brain
  // docs/crm-api.md "The activity log wrote nothing for a year".
  source?: string | null
  is_logged_automatically?: boolean
  // Request context of a system-written row: ip_address, user_agent,
  // controller, action, plus whatever the call site added (counts, changes).
  metadata?: Record<string, unknown> | null

  // Activity details
  task_priority?: 'low' | 'medium' | 'high' | 'urgent'
  task_completed?: boolean
  duration_minutes?: number
  communication_direction?: 'inbound' | 'outbound'
  communication_method?: string
  location?: string
}

export interface SystemActivityFilters {
  search?: string
  user_id?: string
  activity_type?: string
  status?: string
  start_date?: string
  end_date?: string
  page?: number
  per_page?: number
}

export interface SystemActivitiesResponse {
  data: SystemActivity[]
  meta: {
    pagination: {
      current_page: number
      total_pages: number
      total_count: number
      per_page: number
      has_next: boolean
      has_prev: boolean
    }
    activity_summary: Record<string, number>
    user_summary: Record<string, number>
  }
}

export const systemActivitiesApi = {
  /**
   * Get all system activities with filtering
   */
  getActivities: async (filters?: SystemActivityFilters): Promise<SystemActivitiesResponse> => {
    const params = new URLSearchParams()
    
    if (filters?.search) params.append('search', filters.search)
    if (filters?.user_id) params.append('user_id', filters.user_id)
    if (filters?.activity_type) params.append('activity_type', filters.activity_type)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.per_page) params.append('per_page', filters.per_page.toString())

    const response = await api.get(`/activities?${params.toString()}`)
    return response.data
  },

  /**
   * Export activities to CSV
   */
  exportActivities: async (filters?: SystemActivityFilters): Promise<void> => {
    const params = new URLSearchParams()
    
    if (filters?.search) params.append('search', filters.search)
    if (filters?.user_id) params.append('user_id', filters.user_id)
    if (filters?.activity_type) params.append('activity_type', filters.activity_type)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)

    const response = await api.get(`/activities/export?${params.toString()}`, {
      responseType: 'blob',
    })

    // Native OS Save-As dialog in the desktop app; browser download on the web.
    const dateStr = new Date().toISOString().split('T')[0]
    await saveBlob(`system-activities-${dateStr}.csv`, new Blob([response.data]))
  }
}

// Activity type display names
export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  'call': 'Phone Call',
  'email': 'Email',
  'meeting': 'Meeting',
  'note': 'Note',
  'task': 'Task',
  'demo': 'Demo',
  'proposal': 'Proposal',
  'contract_sent': 'Contract Sent',
  'follow_up': 'Follow-up',
  'webinar': 'Webinar',
  'presentation': 'Presentation',
  'training': 'Training',
  'support_ticket': 'Support Ticket',
  'quote_sent': 'Quote Sent',
  'invoice_sent': 'Invoice Sent',
  'payment_received': 'Payment Received',
  'renewal_reminder': 'Renewal Reminder',
  'deal_renewal': 'Deal Renewal',
  'invoice_due': 'Invoice Due',
  'birthday': 'Birthday',
  'social_media_interaction': 'Social Media',
  'survey_response': 'Survey Response',
  'contact_created': 'Contact Created',
  'lead_created': 'Lead Created',
  'customer_created': 'Customer Created',
  'email_campaign_sent': 'Email Campaign Sent',

  // The audit trail. These have been logged by crm-api's controllers all
  // along; until 2026-09-10 the write failed on a missing column, so no row
  // with one of these types had ever reached this page and none of them were
  // labelled. Without a label the table falls back to the raw
  // `customer_bulk_deleted` string.
  'customer_updated': 'Customer Updated',
  'customer_deleted': 'Customer Deleted',
  'customer_bulk_deleted': 'Customers Bulk Deleted',
  'customer_imported': 'Customers Imported',
  'customer_exported': 'Customers Exported',
  'customer_converted': 'Customer Converted',
  'customer_assigned': 'Salesperson Assigned',
  'customer_unassigned': 'Salesperson Unassigned',
  'prospect_created': 'Prospect Created',
  'prospect_updated': 'Prospect Updated',
  'prospect_deleted': 'Prospect Deleted',
  'prospect_bulk_deleted': 'Prospects Bulk Deleted',
  'prospect_imported': 'Prospects Imported',
  'prospect_exported': 'Prospects Exported',
  'prospect_converted': 'Prospect Converted',
  'reseller_created': 'Reseller Created',
  'reseller_updated': 'Reseller Updated',
  'reseller_deleted': 'Reseller Deleted',
  'reseller_imported': 'Resellers Imported',
  'reseller_exported': 'Resellers Exported',
  'dealer_created': 'Dealer Created',
  'dealer_updated': 'Dealer Updated',
  'dealer_deleted': 'Dealer Deleted',
  'deal_created': 'Deal Created',
  'deal_updated': 'Deal Updated',
  'deal_deleted': 'Deal Deleted',
  'invoice_created': 'Invoice Created',
  'invoice_updated': 'Invoice Updated',
  'invoice_deleted': 'Invoice Deleted',
  'invoice_bulk_deleted': 'Invoices Bulk Deleted',
  'invoice_paid': 'Invoice Marked Paid',
  'invoice_unpaid': 'Invoice Marked Unpaid',
  'invoice_schedule_created': 'Invoice Schedule Created',
  'invoice_schedule_updated': 'Invoice Schedule Updated',
  'invoice_schedule_paused': 'Invoice Schedule Paused',
  'invoice_schedule_resumed': 'Invoice Schedule Resumed',
  'invoice_schedule_cancelled': 'Invoice Schedule Cancelled',
  'scheduled_invoice_generated': 'Scheduled Invoice Generated',
  'scheduled_invoice_cancelled': 'Scheduled Invoice Cancelled',
  'user_created': 'User Created',
  'user_updated': 'User Updated',
  'user_deleted': 'User Deleted',
  'user_role_assigned': 'Role Granted',
  'user_role_removed': 'Role Revoked',
  'file_uploaded': 'File Uploaded',
  'file_deleted': 'File Deleted',
  'system_notification': 'System Notification',
  'security': 'Security',
  'login': 'Login',
  'logout': 'Logout'
}

// Activity status display names  
export const ACTIVITY_STATUS_LABELS: Record<string, string> = {
  'scheduled': 'Scheduled',
  'in_progress': 'In Progress',
  'completed': 'Completed',
  'cancelled': 'Cancelled',
  'no_show': 'No Show'
}
